import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { AppConfig } from '../../app/app.config';
import {
  CycleActivationRequest,
  CycleEndRequest,
  CycleChangeDeleteValidation,
  CycleUpdateRequest,
  CyclesPageData,
  CycleSummary
} from '../../models/cycles/cycle.models';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { TimeZoneService } from '../time-zone/time-zone.service';

@Injectable()
export class CyclesService {
  private config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {
  }

  async getActivationProject(date: DateTime): Promise<{projectId: number}> {
    return this.http.get<{projectId: number}>(
      `${this.config.apiUrl}api/appdata/${date.toUTC().toMillis()}/setupsummary/false`
    ).toPromise();
  }

  async getPageData(date: DateTime): Promise<CyclesPageData> {
    const dateUtc = encodeURIComponent(date.toUTC().toISO());
    const result = await this.http
      .get<CyclesPageData>(`${this.config.apiUrl}api/cycles?dateUtc=${dateUtc}`)
      .toPromise();

    return this.deserialize(result);
  }

  async activate(ordinal: number, startDate: DateTime, dailyLogDate: DateTime): Promise<CyclesPageData> {
    const request: CycleActivationRequest = {
      ordinal: ordinal,
      startDateUtc: startDate.toUTC().toISO(),
      startDateOffset: startDate.offset / 60,
      dailyLogDateUtc: dailyLogDate.toUTC().toISO(),
      dailyLogDateOffset: dailyLogDate.offset / 60
    };

    const result = await this.http
      .post<CyclesPageData>(`${this.config.apiUrl}api/cycles/activate`, request)
      .toPromise();

    return this.deserialize(result);
  }

  async end(cycleId: number, endDate: DateTime, dailyLogDate: DateTime, startNextCycle: boolean): Promise<CyclesPageData> {
    const request: CycleEndRequest = {
      cycleId: cycleId,
      endDateUtc: endDate.toUTC().toISO(),
      endDateOffset: endDate.offset / 60,
      dailyLogDateUtc: dailyLogDate.toUTC().toISO(),
      dailyLogDateOffset: dailyLogDate.offset / 60,
      startNextCycle: startNextCycle
    };

    const result = await this.http
      .post<CyclesPageData>(`${this.config.apiUrl}api/cycles/end`, request)
      .toPromise();

    return this.deserialize(result);
  }

  async update(cycleId: number, startDate: DateTime, endDate: DateTime, dailyLogDate: DateTime): Promise<CyclesPageData> {
    const request: CycleUpdateRequest = {
      cycleId: cycleId,
      dailyLogDateUtc: dailyLogDate.toUTC().toISO(),
      dailyLogDateOffset: dailyLogDate.offset / 60
    };
    if (startDate) {
      request.startDateUtc = startDate.toUTC().toISO();
      request.startDateOffset = startDate.offset / 60;
    }
    if (endDate) {
      request.endDateUtc = endDate.toUTC().toISO();
      request.endDateOffset = endDate.offset / 60;
    }
    const result = await this.http.put<CyclesPageData>(`${this.config.apiUrl}api/cycles/update`, request).toPromise();
    return this.deserialize(result);
  }

  async deleteCycle(cycleId: number, dailyLogDate: DateTime): Promise<CyclesPageData> {
    const query = this.dailyLogQuery(dailyLogDate);
    const result = await this.http.delete<CyclesPageData>(`${this.config.apiUrl}api/cycles/${cycleId}?${query}`).toPromise();
    return this.deserialize(result);
  }

  async deleteChange(dailyLogRecordId: number, dailyLogDate: DateTime): Promise<void> {
    const query = this.dailyLogQuery(dailyLogDate);
    await this.http
      .delete<void>(`${this.config.apiUrl}api/cycles/change/${dailyLogRecordId}?${query}`)
      .toPromise();
  }

  async getDeleteChangeValidation(dailyLogRecordId: number): Promise<CycleChangeDeleteValidation> {
    const result = await this.http
      .get<CycleChangeDeleteValidation>(
        `${this.config.apiUrl}api/cycles/change/${dailyLogRecordId}/delete-validation`
      )
      .toPromise();

    if (result && result.blockingCycle) {
      await this.deserializeCycle(result.blockingCycle);
    }
    return result;
  }

  private dailyLogQuery(date: DateTime): string {
    return `dailyLogDateUtc=${encodeURIComponent(date.toUTC().toISO())}&dailyLogDateOffset=${date.offset / 60}`;
  }

  private async deserialize(data: CyclesPageData): Promise<CyclesPageData> {
    if (!data) {
      return new CyclesPageData();
    }

    data.cycles = data.cycles || [];
    for (const cycle of data.cycles) {
      await this.deserializeCycle(cycle);
    }

    data.cycles.sort((left, right) =>
      right.startDateUtc.toMillis() - left.startDateUtc.toMillis() || right.ordinal - left.ordinal
    );

    if (data.activeCycle) {
      const matchingCycle = data.cycles.find(c => c.id === data.activeCycle.id);
      data.activeCycle = matchingCycle || await this.deserializeCycle(data.activeCycle);
    }

    return data;
  }

  private async deserializeCycle(cycle: CycleSummary): Promise<CycleSummary> {
    cycle.startDateUtc = await this.dateParserService.deserializeDate(
      cycle.startDateUtc,
      true,
      this.timeZoneService,
      cycle.startDateOffset
    );

    if (cycle.endDateUtc) {
      cycle.endDateUtc = await this.dateParserService.deserializeDate(
        cycle.endDateUtc,
        true,
        this.timeZoneService,
        cycle.endDateOffset
      );
    }

    cycle.isActive = !cycle.endDateUtc;
    return cycle;
  }
}



// WEBPACK FOOTER //
// ./src/services/cycles/cycles.service.ts